import React, { useEffect, useState } from 'react';
import { 
  Table, 
  Tag, 
  Space, 
  Card, 
  Typography, 
  Input, 
  Select, 
  Button, 
  message, 
  Modal,
  Form
} from 'antd';
import { listFeedbacks, updateFeedback, Feedback } from '../api/feedback';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;
const { TextArea } = Input;

const FeedbackList: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Feedback[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [status, setStatus] = useState<string | undefined>(undefined);
  
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingFeedback, setEditingFeedback] = useState<Feedback | null>(null);
  const [form] = Form.useForm();

  const fetchData = async (page = currentPage, size = pageSize) => {
    setLoading(true);
    try {
      const res = await listFeedbacks({
        page,
        page_size: size,
        status,
      });
      if (res.success) {
        setData(res.data.feedbacks);
        setTotal(res.data.pagination.total);
      }
    } catch (error) {
      message.error('获取反馈列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, status]);

  const handleUpdate = async (values: any) => {
    if (!editingFeedback) return;
    try {
      const res = await updateFeedback(editingFeedback.id, values);
      if (res.success) {
        message.success('更新成功');
        setIsModalVisible(false);
        fetchData();
      }
    } catch (error) {
      message.error('更新失败');
    }
  };

  const showModal = (record: Feedback) => {
    setEditingFeedback(record);
    form.setFieldsValue({
      status: record.status,
      admin_note: record.admin_note,
    });
    setIsModalVisible(true);
  };

  const columns = [
    {
      title: '提交时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text: string) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: 'App',
      dataIndex: 'project_id',
      key: 'project_id',
      width: 120,
      render: (text: string) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: '反馈内容',
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
    },
    {
      title: '联系方式',
      dataIndex: 'contact',
      key: 'contact',
      width: 150,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        let color = 'gold';
        if (status === 'processed') color = 'green';
        if (status === 'archived') color = 'gray';
        return <Tag color={color}>{status.toUpperCase()}</Tag>;
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_: any, record: Feedback) => (
        <Button type="link" onClick={() => showModal(record)}>
          详情 / 处理
        </Button>
      ),
    },
  ];

  return (
    <div className="feedback-list">
      <Card title={<Title level={4} style={{ margin: 0 }}>客诉反馈管理</Title>}>
        <Space style={{ marginBottom: 16 }}>
          <Text>状态筛选：</Text>
          <Select 
            defaultValue="" 
            style={{ width: 120 }} 
            onChange={(val) => {
              setStatus(val || undefined);
              setCurrentPage(1);
            }}
          >
            <Option value="">全部</Option>
            <Option value="unread">未读</Option>
            <Option value="processed">已处理</Option>
            <Option value="archived">已存档</Option>
          </Select>
        </Space>
        
        <Table 
          columns={columns} 
          dataSource={data} 
          loading={loading}
          rowKey="id"
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
            showSizeChanger: true,
          }}
        />
      </Card>

      <Modal
        title="反馈详情"
        visible={isModalVisible}
        onOk={() => form.submit()}
        onCancel={() => setIsModalVisible(false)}
        width={600}
      >
        {editingFeedback && (
          <div style={{ marginBottom: 20 }}>
            <p><strong>设备 ID:</strong> {editingFeedback.device_id}</p>
            <p><strong>设备型号:</strong> {editingFeedback.device_model || 'Unknown'}</p>
            <p><strong>系统版本:</strong> {editingFeedback.system_version || 'N/A'}</p>
            <p><strong>App版本:</strong> {editingFeedback.app_version || 'N/A'}</p>
            <div style={{ background: 'var(--admin-fill)', padding: '12px', borderRadius: '4px', marginTop: '10px' }}>
              <p><strong>反馈内容:</strong></p>
              <p style={{ whiteSpace: 'pre-wrap' }}>{editingFeedback.content}</p>
            </div>
          </div>
        )}
        
        <Form form={form} layout="vertical" onFinish={handleUpdate}>
          <Form.Item name="status" label="更新状态" rules={[{ required: true }]}>
            <Select>
              <Option value="unread">未读 (Unread)</Option>
              <Option value="processed">已处理 (Processed)</Option>
              <Option value="archived">已存档 (Archived)</Option>
            </Select>
          </Form.Item>
          <Form.Item name="admin_note" label="管理备注 (Admin Notes)">
            <TextArea rows={4} placeholder="记录处理意见或备注..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FeedbackList;
